import type { TypedClient } from '@platform/openapi';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ImageKind, MediaView } from './media.ts';
import {
  createUploader,
  putWithProgress,
  UploadFailedError,
  waitFor,
  type UploadPhase,
} from './upload-file.ts';
import { usePageContext } from '../usePageContext.ts';

const POLL_INTERVAL_MS = 1500;
const MAX_POLLS = 80;

export type PendingUpload = {
  readonly key: string;
  readonly file: File;
  readonly preview: string;
  readonly phase: UploadPhase | 'failed';
  readonly progress: number;
  readonly error: string | null;
};

let sequence = 0;

const nextKey = (): string => {
  sequence += 1;
  return `upload-${String(sequence)}`;
};

export const useImageUploads = (api: TypedClient, kind: ImageKind, onReady: (view: MediaView) => void) => {
  const { t, describeError } = usePageContext();
  const [pending, setPending] = useState<readonly PendingUpload[]>([]);
  const aborter = useRef(new AbortController());
  const ready = useRef(onReady);
  const upload = useMemo(
    () =>
      createUploader({
        api,
        put: putWithProgress,
        wait: waitFor,
        intervalMs: POLL_INTERVAL_MS,
        maxPolls: MAX_POLLS,
      }),
    [api],
  );

  useEffect(() => {
    ready.current = onReady;
  });

  useEffect(() => {
    const controller = new AbortController();
    aborter.current = controller;
    return () => {
      controller.abort();
    };
  }, []);

  const update = (key: string, change: Partial<PendingUpload>) => {
    setPending((current) => current.map((entry) => (entry.key === key ? { ...entry, ...change } : entry)));
  };

  const release = (key: string) => {
    setPending((current) => {
      const leaving = current.find((entry) => entry.key === key);
      if (leaving !== undefined) URL.revokeObjectURL(leaving.preview);
      return current.filter((entry) => entry.key !== key);
    });
  };

  const reasonOf = (error: unknown): string =>
    error instanceof UploadFailedError
      ? t(`errors.${error.reason}`)
      : (describeError(error) ?? t('errors.UNKNOWN'));

  const start = (key: string, file: File) => {
    const events = {
      onPhase: (phase: UploadPhase) => {
        update(key, { phase, error: null });
      },
      onProgress: (progress: number) => {
        update(key, { progress });
      },
    };
    upload(file, kind, events, aborter.current.signal)
      .then((view) => {
        if (aborter.current.signal.aborted) return;
        ready.current(view);
        release(key);
      })
      .catch((error: unknown) => {
        if (aborter.current.signal.aborted) return;
        update(key, { phase: 'failed', error: reasonOf(error) });
      });
  };

  return {
    pending,
    add: (files: readonly File[]) => {
      const added = files.map((file) => ({
        key: nextKey(),
        file,
        preview: URL.createObjectURL(file),
        phase: 'hashing' as const,
        progress: 0,
        error: null,
      }));
      setPending((current) => [...current, ...added]);
      for (const entry of added) start(entry.key, entry.file);
    },
    retry: (key: string) => {
      const entry = pending.find((candidate) => candidate.key === key);
      if (entry === undefined) return;
      update(key, { phase: 'hashing', progress: 0, error: null });
      start(key, entry.file);
    },
    dismiss: release,
  };
};
